/**
 * KeyValueGrid — displays an ordered list of label/value pairs.
 *
 * Single responsibility: render a structured record (e.g. an authorization
 * attempt JSON) as an aligned grid. No domain knowledge.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../design-system/tokens.js";

export interface KeyValueEntry {
  /** Stable identifier for React reconciliation. */
  readonly key: string;
  /** Display label (left column). */
  readonly label: string;
  /** Right-column content. */
  readonly value: React.ReactNode;
  /** Render the value in a monospace font (digests, ids, hex). */
  readonly mono?: boolean;
  /** Optional title (native tooltip) on the value. */
  readonly title?: string;
}

export interface KeyValueGridProps {
  readonly entries: readonly KeyValueEntry[];
  /** Use a tighter row gap. */
  readonly dense?: boolean;
}

/** Renders structured key/value pairs with aligned columns. */
export const KeyValueGrid = React.memo(function KeyValueGrid({
  entries,
  dense = false,
}: KeyValueGridProps): React.JSX.Element {
  const rowPadding = dense ? "0.25rem 0" : "0.4rem 0";
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(8rem, max-content) 1fr",
        columnGap: "1rem",
        rowGap: 0,
        margin: 0,
        fontSize: "0.8125rem",
      }}
    >
      {entries.map((entry) => (
        <React.Fragment key={entry.key}>
          <dt
            style={{
              padding: rowPadding,
              color: GRAY[500],
              fontWeight: 500,
              borderBottom: `1px solid ${GRAY[100]}`,
            }}
          >
            {entry.label}
          </dt>
          <dd
            title={entry.title}
            style={{
              padding: rowPadding,
              margin: 0,
              color: GRAY[900],
              fontFamily: entry.mono ? FONT.mono : FONT.body,
              wordBreak: entry.mono ? "break-all" : "normal",
              borderBottom: `1px solid ${GRAY[100]}`,
            }}
          >
            {entry.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
});
